"use client";

import { useEffect, useState, useRef } from "react";
import { useTranslations } from "next-intl";
import { api } from "@/lib/api";
import type { RouteStep, StepCatalogEntry } from "@/lib/types/routing";
import { StepCard } from "./step-card";
import { StepPicker } from "./step-picker";

// Module-level catalog cache
let catalogCache: StepCatalogEntry[] | null = null;

interface StepEditorProps {
  steps: RouteStep[];
  userPlan: string;
  onChange: (steps: RouteStep[]) => void;
}

export function StepEditor({ steps, userPlan, onChange }: StepEditorProps) {
  const t = useTranslations();
  const [catalog, setCatalog] = useState<StepCatalogEntry[]>(catalogCache ?? []);
  const [loading, setLoading] = useState(catalogCache === null);
  const mounted = useRef(true);

  useEffect(() => {
    // catalog/loading state already reflect the cache via the useState
    // initializers, so only a cold cache needs a fetch.
    if (catalogCache !== null) return;
    mounted.current = true;
    api.steps
      .catalog()
      .then((entries) => {
        catalogCache = entries;
        if (mounted.current) {
          setCatalog(entries);
          setLoading(false);
        }
      })
      .catch(() => {
        if (mounted.current) setLoading(false);
      });
    return () => {
      mounted.current = false;
    };
  }, []);

  const catalogByType = catalog.reduce<Record<string, StepCatalogEntry>>(
    (acc, e) => ({ ...acc, [e.type]: e }),
    {}
  );

  const handleAdd = (entry: StepCatalogEntry) => {
    onChange([
      ...steps,
      { type: entry.type, enabled: true, config: {} },
    ]);
  };

  const handleChange = (index: number, step: RouteStep) => {
    const next = [...steps];
    next[index] = step;
    onChange(next);
  };

  const handleRemove = (index: number) => {
    onChange(steps.filter((_, i) => i !== index));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const next = [...steps];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    onChange(next);
  };

  const handleMoveDown = (index: number) => {
    if (index === steps.length - 1) return;
    const next = [...steps];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {steps.length === 0 && (
        <p className="text-sm text-muted-foreground">{t("dashboard.routes.stepEditorNoSteps")}</p>
      )}
      {steps.map((step, i) => (
        <StepCard
          key={`${step.type}-${i}`}
          step={step}
          catalogEntry={catalogByType[step.type]}
          index={i}
          total={steps.length}
          userPlan={userPlan}
          onChange={(s) => handleChange(i, s)}
          onRemove={() => handleRemove(i)}
          onMoveUp={() => handleMoveUp(i)}
          onMoveDown={() => handleMoveDown(i)}
        />
      ))}
      {!loading && (
        <StepPicker catalog={catalog} userPlan={userPlan} onAdd={handleAdd} />
      )}
    </div>
  );
}
